// vendor
import * as React from "react";
import anime from "animejs";
import fastDeepEqual from "fast-deep-equal";
import { useInView } from "react-intersection-observer";
// component
import ThreadReply from "../threadReply";
import ThreadPreview from "../threadPreview";
import ThreadItem from "../threadItem";
import { ThreadWrapper as ThreadStyleWrapper } from "../styledComponents";
import {
  useEventCallback,
  useSafeLayoutEffect,
} from "@vingle/wtf-sdk/helpers/hook";
import { TOP_NAVIGATION_BAR_HEIGHT } from "app/main/constants";

interface IReplyProps {
  visible: boolean;
  paging: Vingle.IPaging;
  isLoading?: boolean;
  threads?: ReadonlyArray<Vingle.IThread>;
  onToggleView: (thread: Vingle.IThread) => void;
  onLoadMoreData: (
    thread: Vingle.IThread,
    pagingDirection: keyof Vingle.IPaging,
    count?: number,
  ) => void;
  onAddThread: (
    thread: Vingle.IThread,
    content: string,
    resource?: Vingle.MediaUpload.IUploadInput | null,
    linkData?: Vingle.LinkUploadData | null,
  ) => void;
  disableLink?: boolean;
  disableUpload?: boolean;
}

interface IProps {
  thread: Vingle.IThread;
  onLike: (thread: Vingle.IThread) => void;
  handleCheckUser: () => boolean;
  handleHighlightUser?: (comment: Vingle.IThread) => boolean;
  reply?: IReplyProps;
}

// tslint:disable-next-line:cognitive-complexity
function useThreadWrapper(props: IProps) {
  const { reply, thread, handleCheckUser, handleHighlightUser } = props;
  const [
    previewReplyAuthor,
    setPreviewReplyAuthor,
  ] = React.useState<Vingle.User.ISimplifiedUser | null>(null);
  const [wrapperRef, wrapperIsInView, wrapperEntity] = useInView({
    threshold: 1,
  });
  const handleToggleView = useEventCallback(() => {
    if (reply) {
      setPreviewReplyAuthor(null);
      reply.onToggleView(thread);
      if (reply.visible && wrapperEntity) {
        requestAnimationFrame(() => {
          const scrollElement =
            (document.scrollingElement as Element) || document.documentElement;
          const wrapperElement = wrapperEntity.target as HTMLDivElement;
          scrollElement.scrollTop =
            wrapperElement.offsetTop - TOP_NAVIGATION_BAR_HEIGHT;
        });
      }
    }
  }, [thread]);
  const handleAddReply = useEventCallback(
    (
      content: string,
      resource?: Vingle.MediaUpload.IUploadInput,
      linkData?: Vingle.LinkUploadData | null,
    ) => {
      if (reply) {
        if (!reply.visible) {
          handleToggleView();
        }
        reply.onAddThread(thread, content, resource, linkData);
        setPreviewReplyAuthor(null);
      }
    },
    [thread, reply],
  );
  const handleReplyWrite = React.useCallback(
    (previewThread: Vingle.IThread) => {
      if (!handleCheckUser()) {
        return;
      }
      if (!previewReplyAuthor || previewReplyAuthor.id !== thread.user.id) {
        setPreviewReplyAuthor(previewThread.user);
      } else {
        setPreviewReplyAuthor(null);
      }
    },
    [previewReplyAuthor, thread, reply],
  );
  const handleLoadMoreReplyData = useEventCallback(
    (pagingDirection: keyof Vingle.IPaging) => {
      if (!reply) {
        return;
      }
      if (!reply.threads) {
        handleToggleView();
      } else {
        reply.onLoadMoreData(thread, pagingDirection);
      }
    },
    [reply, thread, handleToggleView],
  );
  useSafeLayoutEffect(() => {
    if (thread.isPosting && !wrapperIsInView && wrapperEntity) {
      // animation scroll
      const scrollElement =
        (document.scrollingElement as Element) || document.documentElement;
      const wrapperElement = wrapperEntity.target as HTMLDivElement;
      anime({
        targets: scrollElement,
        scrollTop: wrapperElement.offsetTop - TOP_NAVIGATION_BAR_HEIGHT,
        round: 1,
        easing: "easeInSine",
        duration: 500,
      });
    }
  }, [thread.isPosting, Boolean(wrapperEntity)]);
  return {
    handleAddReply,
    handleReplyWrite,
    handleToggleView,
    handleLoadMoreReplyData,
    previewReplyAuthor,
    handleHighlightUser,
    wrapperRef,
  };
}

const ThreadWrapper = React.memo<IProps>(props => {
  const { reply, onLike, thread, handleCheckUser } = props;
  const {
    handleToggleView,
    handleAddReply,
    handleReplyWrite,
    handleLoadMoreReplyData,
    previewReplyAuthor,
    handleHighlightUser,
    wrapperRef,
  } = useThreadWrapper(props);

  if (thread.hidden) {
    return null;
  }

  const visibleReply = Boolean((reply && reply.visible) || previewReplyAuthor);
  const visibleThreads =
    (reply && reply.visible && reply.threads) || thread.previewThreads;
  return (
    <ThreadStyleWrapper isPosting={Boolean(thread.isPosting)} ref={wrapperRef}>
      <ThreadItem
        thread={thread}
        onLike={onLike}
        onReplyWrite={handleReplyWrite}
        handleHighlightUser={handleHighlightUser}
        parentId={thread.id}
      />
      {!visibleReply && thread.previewThreads ? (
        <ThreadPreview
          threadId={thread.id}
          threads={thread.previewThreads}
          threadsCount={thread.replyCount || 0}
          targetReplyAuthor={previewReplyAuthor}
          onLike={onLike}
          onLoadMoreData={handleToggleView}
          onReplyWrite={handleReplyWrite}
          disableLink={reply && reply.disableLink}
          disableUpload={reply && reply.disableUpload}
          onAddThread={handleAddReply}
          handleHighlightUser={handleHighlightUser}
          handleCheckUser={handleCheckUser}
        />
      ) : null}
      {reply && visibleReply && (
        <ThreadReply
          threadId={thread.id}
          threadsCount={thread.replyCount || 0}
          visible={visibleReply}
          threads={visibleThreads}
          paging={reply.paging}
          isLoading={reply.isLoading}
          onToggleView={handleToggleView}
          onLoadMoreData={handleLoadMoreReplyData}
          onAddThread={handleAddReply}
          onWriteReply={handleReplyWrite}
          onLike={onLike}
          targetReplyAuthor={previewReplyAuthor}
          handleCheckUser={handleCheckUser}
          disableLink={reply.disableLink}
          handleHighlightUser={handleHighlightUser}
          disableUpload={reply.disableUpload}
        />
      )}
    </ThreadStyleWrapper>
  );
}, fastDeepEqual);
ThreadWrapper.displayName = "ThreadWrapper";

export default ThreadWrapper;
